import type { MetricsSeriesResponse } from '@app/types/sdk';

export interface MetricsRange {
  from: Date;
  to: Date;
}

export interface KeyedMetricsSeriesResponse {
  cacheKey: string;
  range: MetricsRange;
  data: MetricsSeriesResponse;
}

export type MetricsSeriesCache = Record<string, MetricsSeriesResponse>;

const metricSeriesKey = (labels: Record<string, string>): string =>
  JSON.stringify(Object.entries(labels).sort(([left], [right]) => left.localeCompare(right)));

/**
 * Merges overlapping polling windows so a partial range response cannot erase
 * samples already loaded for the current window. Incoming samples win when a
 * backend returns a corrected value for the same timestamp.
 */
export const mergeMetricsSeriesResponses = (
  previous: MetricsSeriesResponse | null,
  incoming: MetricsSeriesResponse,
  range: MetricsRange
): MetricsSeriesResponse => {
  const rangeStart = range.from.getTime();
  const rangeEnd = range.to.getTime();
  const canMerge = previous?.intent === incoming.intent;
  const seriesByKey = new Map<string, MetricsSeriesResponse['series'][number]>();

  const mergeResponse = (response: MetricsSeriesResponse) => {
    for (const series of response.series ?? []) {
      const key = metricSeriesKey(series.labels ?? {});
      const existing = seriesByKey.get(key);
      const samplesByTime = new Map<number, MetricsSeriesResponse['series'][number]['values'][number]>();
      for (const sample of existing?.values ?? []) {
        samplesByTime.set(sample.t.getTime(), sample);
      }
      for (const sample of series.values ?? []) {
        samplesByTime.set(sample.t.getTime(), sample);
      }
      const values = [...samplesByTime.entries()]
        .filter(([timestamp, sample]) => Number.isFinite(timestamp) && Number.isFinite(sample.value) && timestamp >= rangeStart && timestamp <= rangeEnd)
        .sort(([left], [right]) => left - right)
        .map(([, sample]) => sample);
      if (values.length > 0) {
        seriesByKey.set(key, { labels: series.labels, values });
      }
    }
  };

  if (canMerge && previous) {
    mergeResponse(previous);
  }
  mergeResponse(incoming);

  return {
    intent: incoming.intent,
    series: [...seriesByKey.values()]
  };
};

/** Stores each selected range independently so a shorter range cannot replace a longer one. */
export const mergeKeyedMetricsSeriesResponse = (
  previous: MetricsSeriesCache,
  incoming: KeyedMetricsSeriesResponse
): MetricsSeriesCache => ({
  ...previous,
  [incoming.cacheKey]: mergeMetricsSeriesResponses(previous[incoming.cacheKey] ?? null, incoming.data, incoming.range)
});

/** Never exposes a retained useApiQuery result under a range key it did not originate from. */
export const metricsSeriesForCacheKey = (
  cache: MetricsSeriesCache,
  retainedResponse: KeyedMetricsSeriesResponse | null,
  cacheKey: string
): MetricsSeriesResponse | null => cache[cacheKey] ?? (retainedResponse?.cacheKey === cacheKey ? retainedResponse.data : null);
